<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-grave-stone me-2"></i>{{ $t('page.mortality', 'Mortality & M&M Register') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openDeathModal()"><i class="mdi mdi-plus"></i> Record Death</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.deaths_total || 0 }}</div><div class="hms-stat-lbl">Deaths (period)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.maternal || 0 }}</div><div class="hms-stat-lbl">Maternal</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.perinatal || 0 }}</div><div class="hms-stat-lbl">Perinatal</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.under_5 || 0 }}</div><div class="hms-stat-lbl">Under 5</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.review_pending || 0 }}</div><div class="hms-stat-lbl">Review pending</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='register' }"   @click="tab='register'"><i class="mdi mdi-clipboard-list me-1"></i>Death Register</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }"     @click="tab='detail'" :disabled="!detail.record"><i class="mdi mdi-card-account-details me-1"></i>Case Audit</button>
        <button class="hms-tab" :class="{ active: tab==='conferences' }" @click="tab='conferences'; loadConferences()"><i class="mdi mdi-account-multiple-check me-1"></i>M&amp;M Conferences</button>
        <button class="hms-tab" :class="{ active: tab==='actions' }"    @click="tab='actions'; loadActions()"><i class="mdi mdi-clipboard-check me-1"></i>Action Items</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>Indicators</button>
      </div>

      <!-- ============ DEATH REGISTER ============ -->
      <div v-if="tab==='register'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="period" class="hms-input" @change="loadRegister" style="max-width:160px">
            <option value="last_30d">Last 30 days</option>
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
          <select v-model="reviewFilter" class="hms-input" @change="loadRegister" style="max-width:200px">
            <option value="">All review status</option>
            <option value="pending">Review pending</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Reviewed</option>
            <option value="not_required">Not required</option>
          </select>
          <select v-model="catFilter" class="hms-input" @change="loadRegister" style="max-width:200px">
            <option value="">All categories</option>
            <option value="adult">Adult</option>
            <option value="paediatric">Paediatric</option>
            <option value="neonatal">Neonatal</option>
            <option value="maternal">Maternal</option>
            <option value="perinatal_stillbirth">Perinatal — stillbirth</option>
            <option value="perinatal_early_neonatal">Perinatal — early neonatal</option>
            <option value="older_adult">Older adult</option>
          </select>
        </div>
        <table class="hms-table">
          <thead>
            <tr><th>DC #</th><th>Patient</th><th>Date</th><th>Place</th><th>Category</th><th>Cause</th><th>Flags</th><th>Review</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="r in records" :key="r.mortality_id" :class="rowClass(r)">
              <td><strong>{{ r.death_certificate_no }}</strong></td>
              <td>{{ r.patient_name }}<div class="hms-muted-sm">{{ r.patient_no }}</div></td>
              <td>{{ r.death_date }} {{ r.death_time ? r.death_time.substring(0,5) : '' }}</td>
              <td>{{ formatPlace(r.place_of_death) }}</td>
              <td><span class="hms-pill" :class="catPill(r.patient_category)">{{ formatCat(r.patient_category) }}</span></td>
              <td><span v-if="r.immediate_cause_text">{{ r.immediate_cause_text }}</span><span v-else class="hms-muted-sm">—</span></td>
              <td>
                <span v-if="r.is_maternal == 1" class="hms-tag hms-tag-warn">M</span>
                <span v-if="r.is_perinatal == 1" class="hms-tag hms-tag-warn">P</span>
                <span v-if="r.is_under_5 == 1" class="hms-tag hms-tag-warn">U5</span>
                <span v-if="r.is_in_theatre == 1" class="hms-tag hms-tag-danger">OT</span>
              </td>
              <td><span class="hms-pill" :class="reviewPill(r.review_status)">{{ formatReviewStatus(r.review_status) }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(r)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CASE AUDIT (DETAIL) ============ -->
      <div v-if="tab==='detail' && detail.record">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.record.patient_name }}</div>
                <div class="hms-muted-sm">DC: {{ detail.record.death_certificate_no }} · {{ detail.record.patient_no }} · {{ detail.record.gender }} · DOB {{ detail.record.dob }}</div>
                <div class="hms-muted-sm">Died {{ detail.record.death_date }} {{ detail.record.death_time }} at {{ formatPlace(detail.record.place_of_death) }}</div>
              </div>
              <div class="text-end">
                <div><span class="hms-pill" :class="catPill(detail.record.patient_category)">{{ formatCat(detail.record.patient_category) }}</span></div>
                <div class="hms-muted-sm" v-if="detail.record.admission_number">Admission: {{ detail.record.admission_number }}</div>
                <div class="hms-muted-sm">Certified by: {{ detail.record.certified_by_name || '—' }}</div>
                <div class="hms-muted-sm">Review: <span class="hms-pill" :class="reviewPill(detail.record.review_status)">{{ formatReviewStatus(detail.record.review_status) }}</span></div>
              </div>
            </div>
            <div class="row g-2 mt-2">
              <div class="col-md-6">
                <div class="hms-section-label">CAUSE OF DEATH</div>
                <div><strong>Immediate:</strong> {{ detail.record.immediate_cause_text || '—' }} <span v-if="detail.record.immediate_cause_icd" class="hms-muted-sm">({{ detail.record.immediate_cause_icd }})</span></div>
                <div><strong>Underlying:</strong> {{ detail.record.underlying_cause_text || '—' }} <span v-if="detail.record.underlying_cause_icd" class="hms-muted-sm">({{ detail.record.underlying_cause_icd }})</span></div>
                <div v-if="detail.record.contributing_causes" class="hms-muted-sm">Contributing: {{ detail.record.contributing_causes }}</div>
              </div>
              <div class="col-md-6">
                <div class="hms-section-label">PROCESS</div>
                <div>Autopsy: {{ detail.record.autopsy_done == 1 ? 'Yes' : 'No' }}</div>
                <div>Sent to morgue: {{ detail.record.sent_to_morgue == 1 ? 'Yes' : 'No' }}</div>
                <div>Kin notified: {{ detail.record.kin_notified == 1 ? 'Yes' : 'No' }}</div>
                <div v-if="detail.record.review_classification">Classification: <span class="hms-pill" :class="classificationPill(detail.record.review_classification)">{{ formatClassification(detail.record.review_classification) }}</span></div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openFactorModal()"><i class="mdi mdi-alert-circle"></i> Add avoidable factor</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openActionModal()"><i class="mdi mdi-clipboard-plus"></i> Action item</button>
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openAddToConfModal()"><i class="mdi mdi-account-multiple-plus"></i> Add to M&amp;M agenda</button>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Avoidable factors ({{ detail.factors.length }})</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.factors.length" class="hms-table">
                <thead><tr><th>Category</th><th>Factor</th><th>Avoidability</th></tr></thead>
                <tbody>
                  <tr v-for="f in detail.factors" :key="f.factor_id">
                    <td><span class="hms-pill" :class="factorCatPill(f.factor_category)">{{ f.factor_category }}</span></td>
                    <td>{{ f.description }}<div class="hms-muted-sm">{{ f.factor_subcategory }}</div></td>
                    <td><span class="hms-pill" :class="avoidPill(f.avoidability)">{{ f.avoidability }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No factors identified.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Action items ({{ detail.actions.length }})</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.actions.length" class="hms-table">
                <thead><tr><th>Description</th><th>Owner</th><th>Due</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="a in detail.actions" :key="a.action_id" :class="actionRowClass(a)">
                    <td>{{ a.description }}</td>
                    <td>{{ a.assigned_to_name || a.assigned_to_dept || '—' }}</td>
                    <td>{{ a.due_date || '—' }}</td>
                    <td><span class="hms-pill" :class="actionPill(a.status)">{{ a.status }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No action items.</div>
            </div>
          </div>
        </div>

        <div v-if="detail.conferences.length" class="hms-card mt-3">
          <div class="hms-card-head">Conferences this case appeared in</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Date</th><th>Venue</th><th>Classification</th><th>Learning points</th></tr></thead>
              <tbody>
                <tr v-for="c in detail.conferences" :key="c.cc_id">
                  <td>{{ c.conference_date }}</td>
                  <td>{{ c.venue || '—' }}</td>
                  <td><span class="hms-pill" :class="classificationPill(c.case_classification)">{{ formatClassification(c.case_classification) }}</span></td>
                  <td>{{ c.learning_points || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ M&M CONFERENCES ============ -->
      <div v-if="tab==='conferences'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openConfModal()"><i class="mdi mdi-plus"></i> Schedule M&amp;M</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Date</th><th>Venue</th><th>Chair</th><th>Cases</th><th>Actions</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in conferences" :key="c.conference_id">
              <td><strong>{{ c.conference_date }}</strong> {{ c.conference_time ? c.conference_time.substring(0,5) : '' }}</td>
              <td>{{ c.venue || '—' }}</td>
              <td>{{ c.chairperson_name || '—' }}</td>
              <td>{{ c.cases_reviewed || 0 }}</td>
              <td>{{ c.actions_total || 0 }}</td>
              <td><span class="hms-pill" :class="confPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openConfDetail(c)"><i class="mdi mdi-folder-open"></i> Open</button></td>
            </tr>
          </tbody>
        </table>

        <div v-if="confDetail.conf" class="hms-card mt-3">
          <div class="hms-card-head d-flex justify-content-between">
            <span>{{ confDetail.conf.conference_date }} — {{ confDetail.conf.venue }}</span>
            <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="confDetail.conf = null">Close</button>
          </div>
          <div class="hms-card-body">
            <div class="hms-muted-sm">Chair: {{ confDetail.conf.chairperson_name || '—' }} · Secretary: {{ confDetail.conf.secretary_name || '—' }} · Attendees: {{ confDetail.conf.attendee_count || 0 }}</div>

            <div class="hms-section-label mt-3 d-flex justify-content-between">
              <span>CASES ({{ confDetail.cases.length }})</span>
              <button class="hms-btn-link" @click="loadAgenda(confDetail.conf.conference_id)"><i class="mdi mdi-magic-staff"></i> Auto-build agenda</button>
            </div>
            <table v-if="confDetail.cases.length" class="hms-table">
              <thead><tr><th>DC #</th><th>Patient</th><th>Category</th><th>Cause</th><th>Classification</th><th>Learning points</th></tr></thead>
              <tbody>
                <tr v-for="c in confDetail.cases" :key="c.cc_id">
                  <td><strong>{{ c.death_certificate_no }}</strong></td>
                  <td>{{ c.patient_name }}</td>
                  <td>{{ formatCat(c.patient_category) }}</td>
                  <td>{{ c.immediate_cause_text || c.underlying_cause_text || '—' }}</td>
                  <td>
                    <select :value="c.case_classification" @change="updateCaseClass(c, $event.target.value)" class="hms-input hms-input-sm">
                      <option value="undetermined">Undetermined</option>
                      <option value="non_preventable">Non-preventable</option>
                      <option value="possibly_preventable">Possibly preventable</option>
                      <option value="preventable">Preventable</option>
                    </select>
                  </td>
                  <td>{{ c.learning_points || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-muted-sm">No cases attached. Use "Auto-build agenda" to populate from recent deaths.</div>

            <div v-if="agendaCases" class="mt-3">
              <div class="hms-section-label">SUGGESTED AGENDA — {{ agendaCases.TotalCases }} deaths since {{ agendaCases.AgendaSinceDate }}</div>
              <div v-for="(group, cat) in agendaCases.Grouped" :key="cat" class="mb-2">
                <strong>{{ formatCat(cat) }} ({{ group.length }})</strong>
                <div v-for="c in group" :key="c.mortality_id" class="hms-agenda-row">
                  <span><strong>{{ c.death_certificate_no }}</strong> · {{ c.patient_name }} · {{ c.death_date }}</span>
                  <button v-if="c.already_reviewed != 1" class="hms-btn-link" @click="addCaseToConf(c.mortality_id)">+ Add to conference</button>
                  <span v-else class="hms-muted-sm">already on agenda</span>
                </div>
              </div>
            </div>

            <div class="hms-section-label mt-3">MINUTES &amp; LEARNING SUMMARY</div>
            <textarea v-model="confDetail.conf.minutes" rows="6" class="hms-input" placeholder="Conference minutes — discussion, decisions, votes…"></textarea>
            <textarea v-model="confDetail.conf.learning_summary" rows="3" class="hms-input mt-2" placeholder="Key learning points to share with the team"></textarea>
            <div class="row g-2 mt-2">
              <div class="col-3"><label class="hms-label">Attendees count</label><input v-model.number="confDetail.conf.attendee_count" type="number" class="hms-input" /></div>
            </div>
            <div class="d-flex gap-2 mt-2">
              <button class="hms-btn hms-btn-success" @click="completeConference"><i class="mdi mdi-flag-checkered"></i> Mark conference completed</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ ACTION ITEMS ============ -->
      <div v-if="tab==='actions'">
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ actionStats.open_count || 0 }}</div><div class="hms-stat-lbl">Open</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ actionStats.in_progress || 0 }}</div><div class="hms-stat-lbl">In progress</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ actionStats.closed || 0 }}</div><div class="hms-stat-lbl">Closed</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ actionStats.overdue || 0 }}</div><div class="hms-stat-lbl">Overdue</div></div>
          <div class="hms-stat"><select v-model="actionStatusFilter" class="hms-input" @change="loadActions">
              <option value="">All</option><option value="open">Open</option><option value="in_progress">In progress</option><option value="closed">Closed</option>
            </select></div>
        </div>
        <table class="hms-table">
          <thead><tr><th>Description</th><th>Owner</th><th>Priority</th><th>Due</th><th>Status</th><th>From</th><th></th></tr></thead>
          <tbody>
            <tr v-for="a in actions" :key="a.action_id" :class="actionRowClass(a)">
              <td>{{ a.description }}</td>
              <td>{{ a.assigned_to_name || a.assigned_to_dept || '—' }}</td>
              <td><span class="hms-pill" :class="priorityPill(a.priority)">{{ a.priority }}</span></td>
              <td>{{ a.due_date || '—' }}</td>
              <td><span class="hms-pill" :class="actionPill(a.status)">{{ a.status }}</span></td>
              <td>
                <span v-if="a.death_certificate_no" class="hms-muted-sm">{{ a.death_certificate_no }}</span>
                <span v-else-if="a.conference_date" class="hms-muted-sm">M&amp;M {{ a.conference_date }}</span>
              </td>
              <td>
                <button v-if="a.status !== 'closed'" class="hms-btn hms-btn-success hms-btn-sm" @click="closeAction(a)">Close</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ INDICATORS ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Mortality indicators</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Total deaths</span><strong>{{ indicators.Stats && indicators.Stats.deaths_total || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Crude death rate (deaths / admission %)</span><strong>{{ indicators.CrudeDeathRatePct || 0 }}%</strong></div>
              <div class="hms-ind-row"><span>Maternal Mortality Ratio (per 100,000 LB)</span><strong>{{ indicators.MaternalMortalityRatio || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Perinatal Mortality Rate (per 1,000 LB)</span><strong>{{ indicators.PerinatalMortalityRate || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Under-5 Mortality Rate (per 1,000 LB)</span><strong>{{ indicators.Under5MortalityRate || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Preventable deaths</span><strong class="st-bad">{{ indicators.Stats && indicators.Stats.preventable_deaths || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Avoidable factors by category</div>
            <div class="hms-card-body">
              <div v-if="indicators.FactorCategories && indicators.FactorCategories.length">
                <div v-for="f in indicators.FactorCategories" :key="f.factor_category" class="hms-ind-row">
                  <span><span class="hms-pill" :class="factorCatPill(f.factor_category)">{{ f.factor_category }}</span></span>
                  <strong>{{ f.n }}</strong>
                </div>
              </div>
              <div v-else class="hms-muted hms-pad text-center">No factors logged yet.</div>
            </div>
          </div>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">Top causes of death</div>
          <div class="hms-card-body p-0">
            <table v-if="indicators.TopCauses && indicators.TopCauses.length" class="hms-table">
              <thead><tr><th>#</th><th>Cause</th><th>Deaths</th></tr></thead>
              <tbody>
                <tr v-for="(c, i) in indicators.TopCauses" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ c.cause }}</td>
                  <td><strong>{{ c.deaths }}</strong></td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-muted text-center hms-pad">No cause data for this period.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== RECORD DEATH MODAL ===== -->
    <div v-if="deathModal" class="hms-modal-bg" @click.self="deathModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>Record Death</h5><button class="hms-icon-btn" @click="deathModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!death.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="death.patient_search" class="hms-input" placeholder="Patient name / number" @keyup.enter="findDeathPatient" />
              <button class="hms-btn hms-btn-blue" @click="findDeathPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ death.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="death.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Date *</label><input v-model="death.death_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Time</label><input v-model="death.death_time" type="time" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Place</label>
              <select v-model="death.place_of_death" class="hms-input">
                <option value="ward">Ward</option><option value="theatre">Theatre</option><option value="recovery">Recovery</option>
                <option value="ed">ED</option><option value="doa">DOA</option><option value="on_arrival">On arrival</option>
                <option value="icu">ICU</option><option value="hdu">HDU</option><option value="maternity">Maternity</option>
                <option value="nicu">NICU</option><option value="home">Home</option><option value="in_transit">In transit</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Mode</label>
              <select v-model="death.mode_of_death" class="hms-input">
                <option value="anticipated">Anticipated</option><option value="sudden">Sudden</option>
                <option value="expected_terminal">Expected (terminal)</option><option value="peri_op">Peri-op</option>
                <option value="intra_op">Intra-op</option><option value="during_resuscitation">During resus</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Admission ID (if inpatient)</label><input v-model.number="death.admission_id" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Surgery ID (if peri-op)</label><input v-model.number="death.surgery_id" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Immediate cause of death *</label><input v-model="death.immediate_cause_text" class="hms-input" placeholder="e.g. cardiopulmonary arrest secondary to septic shock" /></div>
            <div class="col-3"><label class="hms-label">Immediate ICD</label><input v-model="death.immediate_cause_icd" class="hms-input" placeholder="A41.9" /></div>
            <div class="col-9"><label class="hms-label">Underlying cause</label><input v-model="death.underlying_cause_text" class="hms-input" placeholder="e.g. severe pneumonia" /></div>
            <div class="col-3"><label class="hms-label">Underlying ICD</label><input v-model="death.underlying_cause_icd" class="hms-input" placeholder="J18.9" /></div>
            <div class="col-9"><label class="hms-label">Contributing causes</label><input v-model="death.contributing_causes" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="death.is_maternal" :true-value="1" :false-value="0" /> Maternal</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="death.is_perinatal" :true-value="1" :false-value="0" /> Perinatal</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="death.autopsy_done" :true-value="1" :false-value="0" /> Autopsy done</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="death.kin_notified" :true-value="1" :false-value="0" /> Kin notified</label></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="death.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="deathModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !death.patient_id || !death.immediate_cause_text" @click="saveDeath">{{ saving?'Recording…':'Record Death' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== AVOIDABLE FACTOR MODAL ===== -->
    <div v-if="factorModal" class="hms-modal-bg" @click.self="factorModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Add avoidable factor</h5><button class="hms-icon-btn" @click="factorModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Category *</label>
              <select v-model="factor.factor_category" class="hms-input">
                <option value="patient">Patient-related</option>
                <option value="health_system">Health-system</option>
                <option value="provider">Provider / clinical management</option>
                <option value="admin">Administrative</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Avoidability</label>
              <select v-model="factor.avoidability" class="hms-input">
                <option value="avoidable">Avoidable</option>
                <option value="probably_avoidable">Probably avoidable</option>
                <option value="not_avoidable">Not avoidable</option>
                <option value="undetermined">Undetermined</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Sub-category</label><input v-model="factor.factor_subcategory" class="hms-input" placeholder="e.g. delayed referral, missed diagnosis, drug stockout" /></div>
            <div class="col-12"><label class="hms-label">Description *</label><textarea v-model="factor.description" class="hms-input" rows="3"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="factorModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !factor.description" @click="saveFactor">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== ACTION ITEM MODAL ===== -->
    <div v-if="actionModal" class="hms-modal-bg" @click.self="actionModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>Action item</h5><button class="hms-icon-btn" @click="actionModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Description *</label><textarea v-model="action.description" class="hms-input" rows="3"></textarea></div>
            <div class="col-6"><label class="hms-label">Owner (user ID)</label><input v-model.number="action.assigned_to_user_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Owner department</label><input v-model="action.assigned_to_dept" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Due date</label><input v-model="action.due_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Priority</label>
              <select v-model="action.priority" class="hms-input"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Status</label>
              <select v-model="action.status" class="hms-input"><option value="open">Open</option><option value="in_progress">In progress</option><option value="closed">Closed</option><option value="cancelled">Cancelled</option></select>
            </div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="actionModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !action.description" @click="saveAction">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== SCHEDULE CONFERENCE MODAL ===== -->
    <div v-if="confModal" class="hms-modal-bg" @click.self="confModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Schedule M&amp;M Conference</h5><button class="hms-icon-btn" @click="confModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Date *</label><input v-model="confForm.conference_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Time</label><input v-model="confForm.conference_time" type="time" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Venue</label><input v-model="confForm.venue" class="hms-input" placeholder="Boardroom / Theatre 1 / Online…" /></div>
            <div class="col-6"><label class="hms-label">Chair (user ID)</label><input v-model.number="confForm.chairperson_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Secretary (user ID)</label><input v-model.number="confForm.secretary_id" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes / agenda preview</label><textarea v-model="confForm.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="confModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveConf">{{ saving?'Scheduling…':'Schedule' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== ADD TO CONFERENCE MODAL ===== -->
    <div v-if="addConfModal" class="hms-modal-bg" @click.self="addConfModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>Add this case to a conference</h5><button class="hms-icon-btn" @click="addConfModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <label class="hms-label">Conference</label>
          <select v-model.number="addConfId" class="hms-input">
            <option :value="null">— select —</option>
            <option v-for="c in conferences.filter(c => c.status !== 'completed')" :key="c.conference_id" :value="c.conference_id">{{ c.conference_date }} — {{ c.venue || 'TBD' }}</option>
          </select>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="addConfModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="!addConfId" @click="confirmAddToConf">Add</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MortalityRegister",
  components: { Navigation },
  data() {
    return {
      tab: 'register', saving: false,
      records: [], stats: {},
      period: 'this_month', reviewFilter: '', catFilter: '',

      detail: { record: null, factors: [], actions: [], conferences: [] },

      conferences: [],
      confDetail: { conf: null, cases: [], actions: [] },
      agendaCases: null,

      actions: [], actionStats: {}, actionStatusFilter: '',

      indicatorPeriod: 'this_month',
      indicators: {},

      deathModal: false, death: this.blankDeath(),
      factorModal: false, factor: this.blankFactor(),
      actionModal: false, action: this.blankAction(),
      confModal: false, confForm: this.blankConfForm(),
      addConfModal: false, addConfId: null
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    blankDeath() {
      return {
        patient_id: null, patient_name: '', patient_search: '',
        death_date: new Date().toISOString().substring(0,10),
        death_time: new Date().toTimeString().substring(0,5),
        place_of_death: 'ward', mode_of_death: 'anticipated',
        admission_id: null, surgery_id: null,
        immediate_cause_text: '', immediate_cause_icd: '',
        underlying_cause_text: '', underlying_cause_icd: '',
        contributing_causes: '',
        is_maternal: 0, is_perinatal: 0,
        autopsy_done: 0, kin_notified: 0,
        notes: ''
      };
    },
    blankFactor() { return { factor_category: 'provider', factor_subcategory: '', description: '', avoidability: 'undetermined' }; },
    blankAction() { return { description: '', assigned_to_user_id: null, assigned_to_dept: '', due_date: '', priority: 'medium', status: 'open' }; },
    blankConfForm() {
      const next = new Date(); next.setDate(next.getDate() + 7);
      return { conference_date: next.toISOString().substring(0,10), conference_time: '14:00', venue: '', chairperson_id: null, secretary_id: null, notes: '' };
    },

    async loadAll() { await this.loadRegister(); },
    async loadRegister() {
      try {
        const r = await executeJson({ function: 'getMortalityRecords', period: this.period, review_status: this.reviewFilter, patient_category: this.catFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.records = d.Records || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openDeathModal() { this.death = this.blankDeath(); this.deathModal = true; },
    async findDeathPatient() {
      const q = (this.death.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.death.patient_id = p.patient_id;
        this.death.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveDeath() {
      this.saving = true;
      try {
        const params = { function: 'recordDeath', ...this.death };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.deathModal = false; alert(r.data.message); await this.loadRegister(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(r) {
      try {
        const res = await executeJson({ function: 'getMortalityDetail', mortality_id: r.mortality_id }, hms);
        if (res.data && res.data.success) {
          const d = res.data.data || {};
          this.detail = { record: d.Record, factors: d.Factors || [], actions: d.Actions || [], conferences: d.Conferences || [] };
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    openFactorModal() { this.factor = this.blankFactor(); this.factorModal = true; },
    async saveFactor() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveAvoidableFactor', mortality_id: this.detail.record.mortality_id, ...this.factor }, hms);
        if (r.data && r.data.success) { this.factorModal = false; await this.openDetail(this.detail.record); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openActionModal() { this.action = this.blankAction(); this.actionModal = true; },
    async saveAction() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveActionItem', mortality_id: this.detail.record.mortality_id, ...this.action }, hms);
        if (r.data && r.data.success) { this.actionModal = false; await this.openDetail(this.detail.record); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openAddToConfModal() {
      if (!this.conferences.length) await this.loadConferences();
      this.addConfId = null;
      this.addConfModal = true;
    },
    async confirmAddToConf() {
      if (!this.addConfId || !this.detail.record) return;
      const r = await executeJson({ function: 'addCaseToConference', conference_id: this.addConfId, mortality_id: this.detail.record.mortality_id }, hms);
      if (r.data && r.data.success) { this.addConfModal = false; await this.openDetail(this.detail.record); }
    },

    // ===== CONFERENCES =====
    async loadConferences() {
      try {
        const r = await executeJson({ function: 'getMmConferences' }, hms);
        if (r.data && r.data.success) this.conferences = (r.data.data && r.data.data.Conferences) || [];
      } catch (e) { console.error(e); }
    },
    openConfModal() { this.confForm = this.blankConfForm(); this.confModal = true; },
    async saveConf() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'scheduleMmConference', ...this.confForm }, hms);
        if (r.data && r.data.success) { this.confModal = false; await this.loadConferences(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async openConfDetail(c) {
      try {
        const r = await executeJson({ function: 'getMmConferenceDetail', conference_id: c.conference_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.confDetail = { conf: d.Conference, cases: d.Cases || [], actions: d.Actions || [] };
          this.agendaCases = null;
        }
      } catch (e) { console.error(e); }
    },
    async loadAgenda(cid) {
      try {
        const r = await executeJson({ function: 'buildMmAgenda', conference_id: cid }, hms);
        if (r.data && r.data.success) this.agendaCases = r.data.data;
      } catch (e) { console.error(e); }
    },
    async addCaseToConf(mid) {
      const r = await executeJson({ function: 'addCaseToConference', conference_id: this.confDetail.conf.conference_id, mortality_id: mid }, hms);
      if (r.data && r.data.success) {
        await this.openConfDetail(this.confDetail.conf);
        await this.loadAgenda(this.confDetail.conf.conference_id);
      }
    },
    async updateCaseClass(c, val) {
      const r = await executeJson({ function: 'updateConferenceCase', cc_id: c.cc_id, case_classification: val }, hms);
      if (r.data && r.data.success) await this.openConfDetail(this.confDetail.conf);
    },
    async completeConference() {
      const r = await executeJson({
        function: 'completeMmConference',
        conference_id: this.confDetail.conf.conference_id,
        minutes: this.confDetail.conf.minutes || '',
        learning_summary: this.confDetail.conf.learning_summary || '',
        attendee_count: this.confDetail.conf.attendee_count || 0
      }, hms);
      if (r.data && r.data.success) { alert('Conference closed.'); await this.loadConferences(); this.confDetail.conf = null; }
    },

    // ===== ACTIONS =====
    async loadActions() {
      try {
        const r = await executeJson({ function: 'getActionItems', status: this.actionStatusFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.actions = d.Actions || [];
          this.actionStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async closeAction(a) {
      const notes = prompt('Closure notes?', 'Resolved');
      if (notes === null) return;
      const r = await executeJson({ function: 'saveActionItem', action_id: a.action_id, status: 'closed', closure_notes: notes }, hms);
      if (r.data && r.data.success) await this.loadActions();
    },

    // ===== INDICATORS =====
    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getMortalityIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) this.indicators = r.data.data || {};
      } catch (e) { console.error(e); }
    },

    // ===== formatting =====
    rowClass(r) {
      if (r.is_maternal == 1 || r.is_perinatal == 1 || r.is_in_theatre == 1) return 'row-action';
      if (r.is_under_5 == 1) return 'row-warn';
      return '';
    },
    formatPlace(p) { return ({ ward: 'Ward', theatre: 'Theatre', recovery: 'Recovery', ed: 'ED', doa: 'DOA', on_arrival: 'On arrival', icu: 'ICU', hdu: 'HDU', maternity: 'Maternity', nicu: 'NICU', home: 'Home', in_transit: 'In transit', other: 'Other' })[p] || p; },
    formatCat(c) { return ({ adult: 'Adult', paediatric: 'Paediatric', neonatal: 'Neonatal', maternal: 'Maternal', perinatal_stillbirth: 'Stillbirth', perinatal_early_neonatal: 'Early neonatal', older_adult: 'Older adult' })[c] || c; },
    catPill(c) { return ({ maternal: 'hms-pill-danger', perinatal_stillbirth: 'hms-pill-danger', perinatal_early_neonatal: 'hms-pill-danger', neonatal: 'hms-pill-warn', paediatric: 'hms-pill-warn', adult: 'hms-pill-info', older_adult: 'hms-pill-info' })[c] || ''; },
    reviewPill(s) { return ({ pending: 'hms-pill-warn', scheduled: 'hms-pill-info', completed: 'hms-pill-success', not_required: 'hms-pill-muted', waived: 'hms-pill-muted' })[s] || ''; },
    formatReviewStatus(s) { return ({ pending: 'Pending', scheduled: 'Scheduled', completed: 'Reviewed', not_required: 'N/A', waived: 'Waived' })[s] || s; },
    classificationPill(c) { return ({ preventable: 'hms-pill-danger', possibly_preventable: 'hms-pill-warn', non_preventable: 'hms-pill-success', undetermined: 'hms-pill-muted' })[c] || ''; },
    formatClassification(c) { return ({ preventable: 'Preventable', possibly_preventable: 'Possibly preventable', non_preventable: 'Non-preventable', undetermined: 'Undetermined' })[c] || c; },
    factorCatPill(c) { return ({ patient: 'hms-pill-info', health_system: 'hms-pill-warn', provider: 'hms-pill-danger', admin: 'hms-pill-muted' })[c] || ''; },
    avoidPill(a) { return ({ avoidable: 'hms-pill-danger', probably_avoidable: 'hms-pill-warn', not_avoidable: 'hms-pill-success', undetermined: 'hms-pill-muted' })[a] || ''; },
    confPill(s) { return ({ scheduled: 'hms-pill-info', in_progress: 'hms-pill-warn', completed: 'hms-pill-success', cancelled: 'hms-pill-muted' })[s] || ''; },
    actionPill(s) { return ({ open: 'hms-pill-warn', in_progress: 'hms-pill-info', closed: 'hms-pill-success', cancelled: 'hms-pill-muted' })[s] || ''; },
    actionRowClass(a) {
      if (a.status === 'closed') return '';
      if (a.due_date && new Date(a.due_date) < new Date()) return 'row-action';
      return '';
    },
    priorityPill(p) { return ({ low: 'hms-pill-muted', medium: 'hms-pill-info', high: 'hms-pill-warn', critical: 'hms-pill-danger' })[p] || ''; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; flex-wrap: wrap; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-input.hms-input-sm { padding: 3px 6px; font-size: 12px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }
.hms-tag { padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; margin-right: 2px; }
.hms-tag-warn { background: #feebc8; color: #7b341e; }
.hms-tag-danger { background: #fed7d7; color: #742a2a; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-agenda-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 12px; border-bottom: 1px solid #edf2f7; font-size: 13px; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad { color: #c53030; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
